import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { getTokens, selectProductTokenByName } from '@rsos/sinatra';
import { fetchOrgTokenSuccess } from '@rsos/sinatra/src/actions/fetchOrgToken';
import { refreshUserTokenSuccess } from '@rsos/sinatra/src/actions/refreshUserToken';
// The next two are imported by path rather than from the barrel, as the two
// imports above already are: the tests here mock `@rsos/sinatra` wholesale, so
// a barrel import has to be added to every such mock factory or every test in
// those files fails with "not a function".
import { selectCurrentOrgId } from '@rsos/sinatra/src/selectors/selectUser';
import { refreshAccessToken } from '@rsos/sinatra/src/utils/tokens';
import { isBootstrappedSession } from '@rsos/sinatra/src/utils/tokens/tokens';
import { getAPIHost, getProductName } from '@rsos/utils/metaTags';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { endSession, resetSessionTeardownForTesting } from './endSession';

const apiHost = getAPIHost();

export const TOKEN_TYPE_ACCESS_TOKEN = 'ACCESS_TOKEN';
export const TOKEN_TYPE_PRODUCT_TOKEN = 'PRODUCT_TOKEN';
export const CURRENT_ORGANIZATION_NAME = 'CURRENT_ORGANIZATION_NAME';

// Re-exported because the tests here and in every consumer reach for it through
// this module, and the teardown it resets now lives in endSession.
export { resetSessionTeardownForTesting };

/**
 * Scorpius prefix for this base query, whose `baseUrl` is the bare API host.
 * The shared refresh writer owns the endpoint path itself.
 */
const SCORPIUS_PATH_PREFIX = 'v1/scorpius/';

const getProductTokenEndpoint = state => {
  const orgID = selectCurrentOrgId(state);
  const productName = getProductName();
  return `v1/scorpius/organizations/${orgID}/token/?product=${productName}`;
};

/**
 * Returns product token and expiration for the current product.
 * @param {Object} sinatra - sinatra state
 */
export const getProductToken = sinatra => {
  const productName = getProductName();
  const { token: productToken, expiration: productTokenExpiration } =
    selectProductTokenByName(sinatra, productName);
  return {
    productToken,
    productTokenExpiration,
  };
};

/**
 * Redux Toolkit baseQueryFn accepted args
 */
export const baseQueryArgs = {
  headers: {
    Authorization: `Bearer ${TOKEN_TYPE_ACCESS_TOKEN}`,
  },
  validateStatus: response => {
    return response.status >= 200 && response.status < 300;
  },
};
/**
 * Base extra options for RSOS API with RTK Query
 */
export const baseExtraOptions = {
  tokenType: TOKEN_TYPE_ACCESS_TOKEN,
};

/**
 * Allow for configuration of which token to use based on the actual
 * Authorization header value. This allows the retry/refresh logic to use the
 * updated store's token values compared to having to hardcode the new token
 * within the retry request.
 * @param {Object} prepareHeaders.headers
 * @param {Object} prepareHeaders.headers.Authorization - possible values are
 * `TOKEN_TYPE_ACCESS` and `TOKEN_TYPE_PRODUCT
 * @param {Object} api - destructured to getState, extra, endpoint, type, forced
 */
const baseQuery = fetchBaseQuery({
  baseUrl: apiHost,
  prepareHeaders: (headers, { getState }) => {
    const sinatra = getState().sinatra;

    if (!headers.get('Authorization')) {
      throw new Error('Must include Authorization header with your request');
    }

    // Use the product/org token
    if (headers.get('Authorization') === `Bearer ${TOKEN_TYPE_PRODUCT_TOKEN}`) {
      const bearerToken = getProductToken(sinatra).productToken;
      headers.set('Authorization', `Bearer ${bearerToken}`);
      return headers;
    }

    const organizationName = getState().sinatra?.user?.currentOrg?.name;
    if (
      headers.get('X-RapidSOS-Org') === CURRENT_ORGANIZATION_NAME &&
      organizationName
    ) {
      headers.set('X-RapidSOS-Org', organizationName);
    }

    // Fall back to using the access token
    const token = sinatra?.auth?.tokens?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/** The refresh succeeded and the original request can be retried. */
export const REFRESH_OUTCOME_REFRESHED = 'refreshed';
/** The credential was rejected (or is unusable); the session is over. */
export const REFRESH_OUTCOME_SESSION_ENDED = 'session-ended';
/** The refresh could not be completed, but the credential is still good. */
export const REFRESH_OUTCOME_RETRYABLE = 'retryable';

/**
 * Synthetic status for a refresh that came back 2xx without a usable token.
 * Such a result has no status of its own, and letting it fall through to the
 * generic `error` bucket would hide it among unrelated faults — the fix is only
 * verifiable if the case has a Sentry issue of its own to watch.
 *
 * This is not supposed to be reachable. Scorpius `RenewTokenView` has a single
 * success return and it always carries a token; every other outcome raises. An
 * unparseable body is reported as PARSING_ERROR before it reaches here. So the
 * remaining ways in are a gateway wrapping the body or a change to the
 * endpoint's response shape.
 *
 * TEMPORARY. This is a probe, expected to stay at zero events. Once it has run
 * in production for a couple of weeks with none, delete this status and let the
 * case fall back into the generic bucket. Keep the token validation itself —
 * that is the actual guard, and it stays regardless. Only the dedicated status
 * and its reporting branch are disposable.
 */
export const REFRESH_STATUS_NO_TOKEN_IN_RESPONSE = 'NO_TOKEN_IN_RESPONSE';

/**
 * Decides whether a failed refresh means the session is over or just that we
 * could not reach a verdict this time.
 *
 * Only a response that actually came back from the server and rejected the
 * request ends a session. Transport errors, timeouts, unparseable responses
 * (typically an HTML error page from a proxy) and 5xx are infrastructure
 * problems: the credential in hand may well still be valid, and destroying the
 * session over them turns a momentary blip into a forced re-login.
 *
 * 4xx other than 401/403 is treated as terminal rather than transient. Such a
 * request will fail identically forever, so retrying would leave the user in a
 * silently frozen session — the worse of the two failure modes.
 * @param {number|string|undefined} status
 * @returns {typeof REFRESH_OUTCOME_SESSION_ENDED | typeof REFRESH_OUTCOME_RETRYABLE}
 */
export const classifyRefreshFailure = status => {
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return REFRESH_OUTCOME_SESSION_ENDED;
  }
  return REFRESH_OUTCOME_RETRYABLE;
};

/**
 * Reports a token-refresh failure to Sentry with the structured status of the
 * refresh call itself (FETCH_ERROR / 401 / 403 / 5xx / …) plus the endpoint
 * whose 401 triggered the refresh. Replaces bare console.error calls whose
 * object argument was mangled to "[object Object]" in Sentry — hiding the very
 * status needed to tell a network blip apart from an expired/revoked token.
 * Fingerprint keeps each (token kind × status) in its own issue.
 *
 * `outcome` goes in tags rather than in the message or the fingerprint, so the
 * existing issues stay the same issues across this change. That matters for
 * verification: the point of the fix is that specific issues (the `access 400`
 * cluster) stop receiving events, which is only evidence if they cannot
 * silently re-form under a new title.
 *
 * `diagnostics` adds per-path context on the same terms: `tags` are indexed and
 * searchable so an issue can be broken down by them, `extra` is not. Neither
 * reaches the message or the fingerprint, so adding a field never splits an
 * existing issue in two.
 * @param {Object} [diagnostics]
 * @param {Record<string, string>} [diagnostics.tags] - low-cardinality, indexed
 * @param {Record<string, unknown>} [diagnostics.extra] - ids and raw values
 */
export const reportTokenRefreshFailure = (
  tokenKind,
  refreshResult,
  api,
  outcome = REFRESH_OUTCOME_SESSION_ENDED,
  diagnostics = {},
) => {
  // `refreshResult` is either RTK Query's typed `{ error: { status } }` shape
  // or a raw thrown error passed straight through (see the `RefreshResult`
  // type in `refreshAccessToken.ts`, whose `failure` field documents both
  // shapes as possible). A raw error carries its own status at the top level
  // rather than nested under `.error`, so both are checked here — otherwise a
  // raw error's status falls back to the generic `error` bucket and gets
  // misclassified as retryable instead of session-ended.
  const rawStatus = refreshResult?.error?.status ?? refreshResult?.status;
  const status =
    typeof rawStatus === 'number' || typeof rawStatus === 'string'
      ? rawStatus
      : 'error';
  captureExceptionWithScope(
    new Error(`[token refresh failed] ${tokenKind} ${status}`),
    {
      tokenKind,
      status,
      outcome,
      originatingEndpoint: api?.endpoint ?? 'unknown',
      error: refreshResult?.error ?? refreshResult,
      ...diagnostics.extra,
    },
    ['token-refresh-error', tokenKind, String(status)],
    { tokenKind, tokenRefreshOutcome: outcome, ...diagnostics.tags },
  );
};

/** No access refresh ran alongside this product-token fetch. */
const ACCESS_OUTCOME_NOT_ATTEMPTED = 'not-attempted';

/** Only the product token was fetched — the caller asked for it by name. */
const REFRESH_PATH_PRODUCT_ONLY = 'product-only';

/**
 * Both tokens were refreshed because the request declared no `tokenType`. A
 * product-token failure can end the session here even though the 401 that
 * started it came from an access-token request.
 */
const REFRESH_PATH_BOTH = 'both';

/**
 * The caller asked for the product token by name, the org-token GET 401'd on
 * the access token it carries as its bearer, and the access token was refreshed
 * before fetching again. Distinct from `both`, which means the request declared
 * no `tokenType` at all, so the two paths stay separable in a breakdown.
 */
const REFRESH_PATH_PRODUCT_AFTER_ACCESS = 'product-after-access';

/**
 * Matches the pre-emptive threshold in `useRenewProductToken`, so `expiring`
 * means "inside the window where the scheduler should already have renewed it"
 * rather than an unrelated number.
 */
const PRODUCT_TOKEN_RENEWAL_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * How much life the product token being replaced had left, as a bucket rather
 * than a duration, because tag values have to stay countable.
 *
 * The distinction that matters is `none` against everything else: a product
 * token that never existed means the fetch has never once succeeded for this
 * org and product, which is a missing grant rather than a rejected credential.
 * `expired` and `expiring` mean a working token reached its end and the renewal
 * was refused, which is the case worth chasing.
 */
const describeProductTokenAge = (productToken, expiration) => {
  if (!productToken) {
    return 'none';
  }
  if (typeof expiration !== 'number' || Number.isNaN(expiration)) {
    return 'unknown';
  }
  const remaining = expiration - Date.now();
  if (remaining <= 0) {
    return 'expired';
  }
  return remaining <= PRODUCT_TOKEN_RENEWAL_THRESHOLD_MS ? 'expiring' : 'fresh';
};

/**
 * Context for a failed product-token fetch, beyond the status and endpoint
 * every token-refresh failure already reports.
 *
 * A product-token 401 ends the session (`classifyRefreshFailure` maps 4xx to
 * session-ended, and `combineOutcomes` lets either token end it), so each of
 * these answers a question needed to judge whether that severity was right:
 *
 * - `accessOutcome` separates a session that was already dead — the access
 *   refresh failed too, so ending it was correct — from one that was healthy
 *   until the product fetch killed it.
 * - `refreshPath` says whether the product token was even asked for. On
 *   `both`, it was refreshed only because the request declared no `tokenType`.
 * - `productTokenAge` separates a never-granted product from a revoked one.
 * - the org and product name are the only way to tell one org's lost grant
 *   apart from a platform-wide fault, and neither appears anywhere else in the
 *   event: the request URL is not captured, and the Sentry user scope carries
 *   an email but no organization.
 *
 * The org id stays in `extra`. Tags are indexed and meant to be aggregated, and
 * an id makes a bad tag value on both counts.
 */
const describeProductRefreshFailure = (
  state,
  { productToken, productTokenExpiration } = {},
  { accessOutcome, refreshPath },
) => ({
  tags: {
    productName: getProductName(),
    refreshPath,
    accessOutcome,
    productTokenAge: describeProductTokenAge(
      productToken,
      productTokenExpiration,
    ),
  },
  extra: {
    organizationId: selectCurrentOrgId(state) ?? 'unknown',
    // Stringified, with the type alongside, because the raw value is ambiguous
    // once it reaches an issue view: prod events pair the tag
    // `productTokenAge: expired` with a `productTokenExpiration` that reads
    // `null`, and a falsy number renders the same way. `0` is a real state
    // here — it is what a product entry whose expiration was never decoded
    // holds, and it buckets as `expired` because it is in the past — so the two
    // cases have to be told apart to know whether the bucket is honest.
    // `"0"`, `"null"` and `"[Filtered]"` are all distinct; `null` is not.
    productTokenExpiration: String(productTokenExpiration),
    productTokenExpirationType: typeof productTokenExpiration,
  },
});

/**
 * Custom query with handling to refresh the access or product token based on
 * `extraOptions.tokenType`. It will refresh both if that isn't passed in to
 * the queryFn
 */
/**
 * In-flight refresh promises, keyed by token kind.
 *
 * A single access-token expiry 401s every request in flight at that moment,
 * and `staggeredBaseQuery` re-enters this whole function on each retry, so
 * without this one expiry fans out into many concurrent refreshes. They race:
 * each one POSTs the same refresh token, and the losers apply a token the
 * server has already rotated away from.
 */
const inFlightRefreshes = new Map();

const withSingleFlight = (tokenKind, run) => {
  const existing = inFlightRefreshes.get(tokenKind);
  if (existing) {
    return existing;
  }
  const pending = run().finally(() => {
    inFlightRefreshes.delete(tokenKind);
  });
  inFlightRefreshes.set(tokenKind, pending);
  return pending;
};

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    const refreshAccessTokenOutcome = () =>
      withSingleFlight(TOKEN_TYPE_ACCESS_TOKEN, async () => {
        const refreshToken = getTokens()?.refresh_token;

        const refreshed = await refreshAccessToken({
          refreshToken,
          transport: (path, body) =>
            baseQuery(
              {
                url: `${SCORPIUS_PATH_PREFIX}${path}`,
                // The endpoint reads the body, not this header —
                // `prepareHeaders` replaces the value below with the (expired)
                // access token before the request leaves. It still has to be
                // set to something: `prepareHeaders` throws outright on a
                // missing Authorization header. Reconciling that belongs with
                // the wider token-source cleanup, not with this change.
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
                method: 'POST',
                body,
              },
              api,
              extraOptions,
            ),
        });

        switch (refreshed.status) {
          case 'refreshed':
            // The writer has already persisted it; Redux is this caller's to
            // update, and `prepareHeaders` reads the outgoing token from there.
            api.dispatch(refreshUserTokenSuccess(refreshed.token));
            return REFRESH_OUTCOME_REFRESHED;

          // IRP sessions and legacy `?access_token=` auto-login links have no
          // refresh token. Posting one anyway is a guaranteed 400 from the
          // server, and the old code read that 400 as "credential rejected"
          // and logged the user out. Nothing was sent, so say so directly
          // instead of asking the server to tell us.
          //
          // The session still ends either way — there is no way to carry on
          // without a token — but only an ordinary session that lost its
          // refresh token is a fault worth reporting. A link-bootstrapped one
          // was issued `refresh_token: null` on purpose, so reaching the end of
          // its access token is the designed behavior, and capturing it made it
          // the largest error cohort in this area for no actionable reason.
          case 'no-refresh-token':
            if (!isBootstrappedSession()) {
              reportTokenRefreshFailure(
                'access',
                { error: { status: 'NO_REFRESH_TOKEN' } },
                api,
                REFRESH_OUTCOME_SESSION_ENDED,
              );
            }
            return REFRESH_OUTCOME_SESSION_ENDED;

          // A 2xx whose body carries no token is a failed refresh wearing a
          // success status. It has no status of its own, and an absent status
          // classifies as retryable, which is the right answer: the credential
          // in hand was never rejected.
          case 'no-token-in-response': {
            const failure = {
              error: { status: REFRESH_STATUS_NO_TOKEN_IN_RESPONSE },
            };
            const outcome = classifyRefreshFailure(failure.error.status);
            reportTokenRefreshFailure('access', failure, api, outcome);
            return outcome;
          }

          default: {
            // `refreshAccessToken`'s in-flight promise (refreshAccessToken.ts)
            // is de-duplicated across every caller in the bundle, not just
            // this one — so this call can join a refresh actually driven by
            // another caller's transport (the axios-based ones in
            // createApiInstance.js, midasInstance.ts, or
            // actions/refreshUserToken.js) and get back whatever *that*
            // transport produced. `failure` is therefore either an RTK Query
            // result (`{ error: { status } }`, if this caller's own
            // fetchBaseQuery-based transport ran) or a raw thrown error
            // (e.g. an axios error, if one of those other callers' transport
            // won the race instead), which carries its status at the top
            // level rather than nested under `.error`. Both are checked so
            // the outcome agrees with the status `reportTokenRefreshFailure`
            // reports below.
            const outcome = classifyRefreshFailure(
              refreshed.failure?.error?.status ?? refreshed.failure?.status,
            );
            reportTokenRefreshFailure(
              'access',
              refreshed.failure,
              api,
              outcome,
            );
            return outcome;
          }
        }
      });

    /**
     * Performs the org-token GET, authorized by the access token in storage.
     *
     * Split out from `settleProductToken` below so a caller can see the raw
     * result before it is classified and reported: a 401 here is the server
     * rejecting the bearer, which is the access token, and that is worth
     * retrying behind an access refresh rather than reporting as a
     * product-token failure.
     *
     * Not single-flighted itself — the entry points below hold the flight for
     * the whole refresh they drive, so a caller that fetches twice keeps both
     * fetches inside one flight.
     */
    const fetchProductToken = async () => {
      const userToken = getTokens()?.token;
      // Read before the request so a failure reports the token being
      // replaced rather than whatever the failed call left behind.
      const productTokenState = getProductToken(api.getState()?.sinatra);
      const result = await baseQuery(
        {
          url: getProductTokenEndpoint(api.getState()),
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        },
        api,
        extraOptions,
      );
      return { result, productTokenState };
    };

    /**
     * Stores a fetched product token, or classifies and reports the failure.
     * @param {Object} attempt - The return of `fetchProductToken`
     * @param {string} accessOutcome - What the access refresh did, if anything
     * @param {string} refreshPath - Which path reached this fetch
     */
    const settleProductToken = (
      { result, productTokenState },
      accessOutcome,
      refreshPath,
    ) => {
      // Deliberately not validated the way the access token is. These two
      // endpoints do not share semantics: the refresh endpoint answers with
      // a token or raises, so a 2xx without one means something broke, while
      // the org-token endpoint is documented in fetchOrgToken as sometimes
      // answering 200 with a non-string token for an org that has none.
      // There, an absent token is a legitimate state rather than a fault,
      // and rejecting it would end sessions over an expected response.
      // Whether an empty product token should still count as a refresh is a
      // real question, but it is a behavior decision rather than a bug fix.
      if (result.data) {
        const productToken = result.data.token;
        // The product name is what `productsReducer` keys the token by, and
        // what `getProductToken` reads back. Omitting it defaults the name to
        // null, storing the fresh token under `byName['null']` where nothing
        // looks for it: the retry then replays the same stale token, 401s
        // again, exhausts the retry budget, and ends the session.
        api.dispatch(fetchOrgTokenSuccess(productToken, getProductName()));
        return REFRESH_OUTCOME_REFRESHED;
      }

      const outcome = classifyRefreshFailure(result?.error?.status);
      reportTokenRefreshFailure(
        'product',
        result,
        api,
        outcome,
        describeProductRefreshFailure(api.getState(), productTokenState, {
          accessOutcome,
          refreshPath,
        }),
      );
      return outcome;
    };

    const refreshProductToken = (
      accessOutcome = ACCESS_OUTCOME_NOT_ATTEMPTED,
      refreshPath = REFRESH_PATH_PRODUCT_ONLY,
    ) =>
      withSingleFlight(TOKEN_TYPE_PRODUCT_TOKEN, async () =>
        settleProductToken(
          await fetchProductToken(),
          accessOutcome,
          refreshPath,
        ),
      );

    /**
     * Combines the outcomes of refreshing both tokens. The session ends only if
     * one of them was actually rejected; if either was merely unreachable we
     * keep the session and let the caller retry.
     */
    const combineOutcomes = (accessOutcome, productOutcome) => {
      if (
        accessOutcome === REFRESH_OUTCOME_SESSION_ENDED ||
        productOutcome === REFRESH_OUTCOME_SESSION_ENDED
      ) {
        return REFRESH_OUTCOME_SESSION_ENDED;
      }
      if (
        accessOutcome === REFRESH_OUTCOME_RETRYABLE ||
        productOutcome === REFRESH_OUTCOME_RETRYABLE
      ) {
        return REFRESH_OUTCOME_RETRYABLE;
      }
      return REFRESH_OUTCOME_REFRESHED;
    };

    /**
     * Refreshes the product token for a request that named `productToken` as
     * its `tokenType`.
     *
     * The org-token GET carries the *access* token as its bearer, so a 401 from
     * it is the server rejecting that token and says nothing about the product
     * grant. This path used to end the session on that first 401 without ever
     * attempting an access refresh, which logged dispatchers out over a stale
     * access token that a refresh would have fixed. Refresh and fetch again
     * before reporting anything or tearing the session down.
     *
     * Only a 401 is retried. `classifyRefreshFailure` treats every other 4xx as
     * terminal because such a request fails identically forever, and a fresh
     * access token changes nothing about a 403 or a 404.
     */
    const refreshProductTokenWithAccessRetry = () =>
      withSingleFlight(TOKEN_TYPE_PRODUCT_TOKEN, async () => {
        const attempt = await fetchProductToken();
        if (attempt.result.data || attempt.result?.error?.status !== 401) {
          return settleProductToken(
            attempt,
            ACCESS_OUTCOME_NOT_ATTEMPTED,
            REFRESH_PATH_PRODUCT_ONLY,
          );
        }

        const accessOutcome = await refreshAccessTokenOutcome();
        if (accessOutcome !== REFRESH_OUTCOME_REFRESHED) {
          // There is no fresher access token to fetch with, so the 401 stands
          // unexamined. Report it, then let the access outcome alone decide the
          // session's fate. Combining the two would end the session on a 401
          // that only ever described a bearer whose own refresh could not be
          // reached — a network blip turned into a forced re-login.
          settleProductToken(
            attempt,
            accessOutcome,
            REFRESH_PATH_PRODUCT_AFTER_ACCESS,
          );
          return accessOutcome;
        }

        return settleProductToken(
          await fetchProductToken(),
          accessOutcome,
          REFRESH_PATH_PRODUCT_AFTER_ACCESS,
        );
      });

    let outcome;

    try {
      if (extraOptions?.tokenType === TOKEN_TYPE_ACCESS_TOKEN) {
        outcome = await refreshAccessTokenOutcome();
      } else if (extraOptions?.tokenType === TOKEN_TYPE_PRODUCT_TOKEN) {
        outcome = await refreshProductTokenWithAccessRetry();
      } else {
        // No declared `tokenType`, so both tokens are refreshed even though
        // only one of them authorized the request that 401'd. The access
        // outcome is passed on so a product failure reported from here records
        // whether the session was still healthy when it ended.
        const accessOutcome = await refreshAccessTokenOutcome();
        outcome = combineOutcomes(
          accessOutcome,
          await refreshProductToken(accessOutcome, REFRESH_PATH_BOTH),
        );
      }
    } catch (error) {
      captureExceptionWithScope(
        error instanceof Error
          ? error
          : new Error(`[token refresh failed] exception ${String(error)}`),
        {
          phase: 'token-refresh-exception',
          originatingEndpoint: api?.endpoint ?? 'unknown',
        },
        ['token-refresh-error', 'exception'],
      );
      // An exception here is a bug in this code, not a verdict from the server
      // about the credential. Keep the session and let the request retry.
      outcome = REFRESH_OUTCOME_RETRYABLE;
    }

    if (outcome === REFRESH_OUTCOME_RETRYABLE) {
      // The credential was never rejected — we just could not reach a verdict
      // (network dropped, gateway 5xx, unparseable response). Destroying the
      // session here is what turns a momentary blip into a forced re-login.
      // Surface the original 401 so `staggeredBaseQuery` retries it.
      return result;
    }

    // Handle refresh failure - log user out
    if (outcome === REFRESH_OUTCOME_SESSION_ENDED) {
      // Idempotent and shared with the socket path, so a single expiry that
      // reaches here from several requests at once — and from the Hermes
      // clients at the same moment — still produces one notice and one logout.
      // `api.dispatch` belongs to the store that issued this request, so the
      // teardown lands in the right store without anything being registered in
      // advance.
      await endSession(api.dispatch);

      // Stop the retry wrapper dead rather than returning the error normally.
      // `retry`'s default condition is `attempt <= maxRetries` and never looks
      // at the status, so a returned 401 gets retried — running this whole
      // teardown once per attempt: three storage wipes, three toasts, three
      // logout navigations, three Sentry events. `retry.fail` sets
      // `throwImmediately`, which the wrapper honours by returning the value
      // as-is. The session is over; there is nothing to retry.
      return retry.fail(result.error, result.meta);
    }

    // This will use the refreshed token for the retry
    result = await baseQuery(args, api, extraOptions);
  }

  return result;
};

/**
 * RTK automatic retry utility with exponential backoff. With no `retryCondition`
 * configured, RTK Query retries on *every* failed query — network FETCH_ERRORs
 * and TIMEOUT_ERRORs included, not just 5xx responses — up to `maxRetries`
 * before the rejection finally surfaces to the caller.
 */
export const staggeredBaseQuery = retry(baseQueryWithReauth, {
  maxRetries: 2,
});

export const rsosApi = createApi({
  reducerPath: 'api',
  baseQuery: staggeredBaseQuery,
  endpoints: () => ({}),
});
